import { Component, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Edition, ExtraService } from '@core/models/edition.model';
import { TPVService } from '@core/services/tpv.service';
import { NavController } from '@ionic/angular';
import { combineLatest, first, map, Observable } from 'rxjs';
import { EditionService } from '@core/use-case-services/edition.service';
import { InscriptionService } from '@core/use-case-services/inscription.service';
import { ENVIRONMENT } from '@core/core.constants';
import { UiService } from '@core/services/ui.service';
import { PaymentMethod } from '@core/models/common.model';
import { Inscription } from '@core/models/inscription.model';

type ExtraServiceWithContext = ExtraService & {
  neededQuantity: number;
  foundQuantity: number;
  participants: number;
  edition: Edition;
  inscription: Inscription;
};

@Component({
  selector: 'gt-add-payment',
  templateUrl: './payment.page.html',
  styleUrls: ['./payment.page.scss'],
})
export class AddPaymentPage {
  loading = false;
  navController = inject(NavController);
  editionService = inject(EditionService);
  participantInscriptionService = inject(InscriptionService);
  route = inject(ActivatedRoute);
  tpvService = inject(TPVService);
  uiService = inject(UiService);
  environment = inject(ENVIRONMENT);

  public total = 0;
  public services: { id: string; quantity: number; price: number }[] = [];
  public editionExtraServices: ExtraService[] = [];
  public paymentMethod!: PaymentMethod;

  private myCurrentInscription$ = this.participantInscriptionService.myCurrentInscription$;
  private currentEdition$ = this.editionService.currentEdition$;
  public myCurrentPayments$ = this.participantInscriptionService.myCurrentPayments$;

  public extraServices$: Observable<ExtraServiceWithContext[]> = combineLatest([
    this.currentEdition$,
    this.myCurrentInscription$,
  ]).pipe(
    map(([edition, inscription]) => {
      if (!inscription || !edition) throw new Error('Something went wrong');
      const filteredExtraServices = edition.extraServices
        .map((extraService) => {
          const found = inscription.extraServices.find((iExtraService) => iExtraService.id === extraService.id);
          const neededQuantity = extraService.mandatory
            ? extraService.byPerson
              ? (inscription.summonedParticipants?.length ?? 0) - (found?.quantity ?? 0)
              : 1
            : 0;
          return {
            ...extraService,
            neededQuantity,
            foundQuantity: found?.quantity ?? 0,
            participants: inscription.summonedParticipants?.length ?? 0,
            edition,
            inscription,
          };
        })
        .filter((service) => {
          if (!service.byPerson && service.foundQuantity >= 1) return false;
          if (service.byPerson && service.foundQuantity >= service.participants) return false;
          return true;
        });

      this.services = filteredExtraServices.map((s) => ({
        id: s.id,
        quantity: 0,
        price: s.price,
      }));
      this.editionExtraServices = filteredExtraServices;

      return filteredExtraServices;
    }),
  );

  reCalcRate(extraService: ExtraService, isAdding = true) {
    const subTotal = extraService.price;
    if (isAdding) {
      this.total = this.total + subTotal;
    } else {
      this.total = this.total - subTotal;
    }
  }

  async confirm() {
    if (this.loading) return;
    this.loading = true;
    await this.uiService.showLoading();
    combineLatest([this.currentEdition$, this.myCurrentInscription$])
      .pipe(first())
      .subscribe(([edition, inscription]) => {
        if (!inscription || !edition) throw new Error('Something went wrong');
        this.tpvService
          .createPaymentIntent({
            inscriptionId: inscription.id,
            editionId: edition.id,
            extraServices: this.participantInscriptionService.prepareExtraServices(
              this.services,
              this.editionExtraServices,
            ),
            appType: this.environment.appType,
            type: this.paymentMethod,
            rate: null,
          })
          .then(() => {
            setTimeout(() => this.uiService.hideLoading(), 5_000);
            this.loading = false;
          })
          .catch((e) => {
            this.uiService.showToast('ERROR.' + e.message, 'danger');
            this.uiService.hideLoading();
            this.loading = false;
          });
      });
  }
}
